import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

import HeaderNav from "../components/HeaderNav";
import ResumeIntake from "../components/ResumeIntake";
import CareerSnapshot from "../components/CareerSnapshot";
import ResumeHealth from "../components/ResumeHealth";
import TopRolesTarget from "../components/TopRolesTarget";
import CareerPyramid from "../components/future/CareerPyramid";
import SkillGapPreview from "../components/SkillGapPreview";
import ImmediateActions from "../components/ImmediateActions";
import UpgradeBridges from "../components/UpgradeBridges";
import TargetedResumeWorkbench from "../components/TargetedResumeWorkbench";

import { uploadInChunks } from "../lib/uploads";
import MembershipBanner from "../components/MembershipBanner";
import ApplicationTarget from "../components/ApplicationTarget";
import ApplicationTracker from "../components/ApplicationTracker";
import ResumeBuilderWizard from "../components/builder/ResumeBuilderWizard";
const AppPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState("intake"); // intake | builder | dashboard | workbench
  const [analyzing, setAnalyzing] = useState(false);
  const [step, setStep] = useState(0);
  const [resumeId, setResumeId] = useState(null);
  const [filename, setFilename] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [generatingRoleId, setGeneratingRoleId] = useState(null);
  const [gen, setGen] = useState(null);
  const [progress, setProgress] = useState(0);
  const uploadController = useRef(null);
  const activeUpload = useRef(null);
  const [membership, setMembership] = useState(null);
  const refreshMembership = useCallback(() => api.get("/membership").then(r => setMembership(r.data)).catch(() => {}), []);
  useEffect(() => { if (user) refreshMembership(); }, [user, refreshMembership]);
  const [config, setConfig] = useState({ show_upgrade_ctas: true, announcement: "", maintenance_mode: false });
  const [recent, setRecent] = useState([]);
  const [opening, setOpening] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const deleteSaved = async (item) => {
    if (!item) return;
    setPendingDelete(null);
    try {
      await api.delete(`/resumes/${item.resume_id}`);
      setRecent((list) => list.filter((r) => r.resume_id !== item.resume_id));
      toast.success("Resume removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove that resume");
    }
  };

  useEffect(() => {
    if (user && view === "intake") api.get("/resumes").then(r => setRecent(r.data)).catch(() => {});
  }, [user, view]);

  const openSaved = async (item) => {
    setOpening(item.resume_id);
    try {
      const { data } = await api.get(`/resumes/${item.resume_id}/dashboard`);
      setResumeId(data.resume_id);
      setFilename(data.filename);
      setDashboard(data.dashboard);
      setView("dashboard");
      window.scrollTo(0, 0);
    } catch {
      toast.error("Could not open that analysis. Please try again.");
    } finally {
      setOpening(null);
    }
  };

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
    else if (!loading && user?.is_admin && !sessionStorage.getItem("tj-skip-admin-redirect")) navigate("/admin", { replace: true });
  }, [loading, user, navigate]);

  const cancelAnalyze = () => {
    uploadController.current?.abort();
    uploadController.current = null;
    if (activeUpload.current) api.delete(`/uploads/${activeUpload.current}`).catch(() => {});
    activeUpload.current = null;
    setAnalyzing(false);
    setProgress(0);
  };

  const templateOnly = async (rid) => {
    try {
      const { data } = await api.post(`/resumes/${rid}/reformat`);
      const saved = recent.find((r) => r.resume_id === rid);
      setResumeId(rid);
      setFilename(saved?.filename || "Your resume");
      setGen({ genId: data.gen_id, roleTitle: data.display_title || "Your resume", resume: data.resume, mode: "reformat", request: null, key: "reformat" });
      setView("workbench");
      window.scrollTo(0, 0);
      toast.success("Content untouched — choose any template on your plan and download");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open your resume for a template change");
    }
  };

  const analyze = async (file, details = {}) => {
    const controller = new AbortController();
    uploadController.current = controller;
    setAnalyzing(true);
    setStep(0);
    setProgress(0);
    try {
      const uploadId = await uploadInChunks(file, "resume", { signal: controller.signal, onProgress: setProgress, onInitialize: id => { activeUpload.current = id; } });
      setStep(2);
      const form = new FormData();
      form.append("upload_id", uploadId);
      form.append("jd_text", details.jd || "");
      form.append("company_name", details.company || "");
      form.append("target_role_title", details.roleTitle || "");
      form.append("experience_summary", details.ownSummary || "");
      form.append("job_preference", details.preference || "");
      form.append("job_preference_note", details.preferenceNote || "");
      form.append("current_ctc", details.currentCtc || "");
      form.append("expected_ctc", details.expectedCtc || "");
      form.append("salary_currency", details.salaryCurrency || "INR");
      const res = await api.post("/resumes/upload", form, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setResumeId(res.data.resume_id);
      setFilename(file.name);
      setDashboard(res.data.dashboard);
      setView("dashboard");
      toast.success("Analysis complete — your dashboard is ready");
    } catch (e) {
      if (!controller.signal.aborted) toast.error(e?.response?.data?.detail || "Analysis failed. Please try again.");
    } finally {
      if (uploadController.current === controller) {
        setAnalyzing(false);
        setStep(0);
        uploadController.current = null;
        activeUpload.current = null;
      }
    }
  };

  const generateResume = async (request, key) => {
    setGeneratingRoleId(key);
    try {
      const res = await api.post(`/resumes/${resumeId}/generate`, request);
      setGen({ genId: res.data.gen_id, roleTitle: res.data.role.title, resume: res.data.resume, mode: res.data.mode, request, key });
      setView("workbench");
      window.scrollTo(0, 0);
      toast.success(res.data.cached ? "Your saved resume is ready" : "Your resume is ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGeneratingRoleId(null);
      refreshMembership();
    }
  };
  const createForRole = (role) => generateResume({ role_id: role.role_id }, role.role_id);
  const createForInferredRole = () => generateResume({ mode: "improve" }, "__inferred__");
  const createForTarget = () => generateResume({ mode: "target" }, "__target__");
  const createForJob = (job) => generateResume({ mode: "job", job_id: job.id }, job.id);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  const snap = dashboard?.career_snapshot;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <HeaderNav filename={filename} onHome={() => { setView("intake"); setDashboard(null); setResumeId(null); setFilename(""); }} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        <MembershipBanner membership={membership} onRefresh={refreshMembership} />
        {config.announcement && (
          <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-700" data-testid="announcement-banner">
            {config.announcement}
          </div>
        )}
        {config.maintenance_mode && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700" data-testid="maintenance-banner">
            We're performing maintenance. Some features may be temporarily unavailable.
          </div>
        )}
        {view === "intake" && (
          <div className="max-w-2xl mx-auto space-y-4">
            <ResumeIntake onAnalyze={analyze} onCancel={cancelAnalyze} loading={analyzing} step={step} progress={progress}
              onBuildFromScratch={() => { setView("builder"); window.scrollTo(0, 0); }}
              onTemplateOnly={templateOnly} savedResumes={recent} />
            {!!recent.length && (
              <section className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5" data-testid="recent-analyses">
                <h3 className="font-bold text-slate-900 text-base">Your recent analyses</h3>
                <p className="text-xs text-slate-500 mt-1">Reopen a dashboard without re-uploading. Opening a saved analysis never uses your allowance.</p>
                <ul className="mt-3 divide-y divide-slate-200">
                  {recent.slice(0, 5).map((r) => (
                    <li key={r.resume_id} className="flex items-center gap-2">
                      <button onClick={() => openSaved(r)} disabled={opening === r.resume_id} className="flex-1 flex items-center justify-between gap-3 py-3 text-left" data-testid={`open-saved-resume-${r.resume_id}`}>
                        <span className="min-w-0">
                          <span className="block text-sm text-slate-800 break-words">{r.filename}</span>
                          <span className="block text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</span>
                        </span>
                        <span className="shrink-0 text-xs text-sky-700">{opening === r.resume_id ? "Opening…" : "Open"}</span>
                      </button>
                      <button onClick={() => setPendingDelete(r)} title="Remove this resume" aria-label={`Remove ${r.filename}`}
                        className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                        data-testid={`delete-saved-resume-${r.resume_id}`}>
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            <AlertDialog open={!!pendingDelete} onOpenChange={(open) => { if (!open) setPendingDelete(null); }}>
              <AlertDialogContent data-testid="delete-resume-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove this resume?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {pendingDelete?.filename} and every resume generated from it will be deleted. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-resume-cancel">Keep it</AlertDialogCancel>
                  <AlertDialogAction onClick={() => deleteSaved(pendingDelete)} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-resume-confirm">Remove</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}

        {view === "builder" && (
          <div className="max-w-3xl mx-auto">
            <ResumeBuilderWizard
              onExit={() => setView("intake")}
              onDone={(data) => {
                setResumeId(data.resume_id);
                setDashboard(data.dashboard);
                setFilename("Built with tomorrowsjob");
                setView("dashboard");
                window.scrollTo(0, 0);
                refreshMembership();
              }}
            />
          </div>
        )}

        {view === "dashboard" && dashboard && (
          <div className="space-y-4">
            {dashboard.data_sufficiency === "limited" && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700" data-testid="limited-data-banner">
                We found limited career information in this resume. Some fields (like target role) may show "Not enough information." We've still given resume-quality feedback below without inventing anything.
              </div>
            )}
            <div className="flex items-center justify-between">
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Your Career Intelligence</h1>
              <button onClick={() => setView("intake")} className="text-sm text-sky-600 hover:text-sky-700" data-testid="new-analysis-button">New analysis</button>
            </div>

            {/* Nested layout matching reference. Row 1: Snapshot + Actions. Row 2: Resume Health | (Top Roles then Skill Gap + Unlock) */}
            <div className="space-y-4">
              <div className="grid gap-4 lg:grid-cols-4 items-stretch">
                <div className="lg:col-span-3">{snap && <CareerSnapshot snapshot={snap} compensation={dashboard.compensation} onCreate={createForInferredRole} generating={generatingRoleId === "__inferred__"} disabled={!!generatingRoleId} />}</div>
                <div className="lg:col-span-1">
                  <ApplicationTarget target={dashboard.application_target} inferredTitle={snap?.inferred_target_role} onCreate={createForTarget} onAdd={() => setView("intake")} disabled={!!generatingRoleId} generating={generatingRoleId === "__target__"} />
                </div>
              </div>
              <div className="grid gap-4 lg:grid-cols-4 items-start">
                <div className="lg:col-span-1"><ResumeHealth health={dashboard.resume_health} /></div>
                <div className="lg:col-span-3">
                  <TopRolesTarget resumeId={resumeId} target={dashboard.application_target} roles={dashboard.role_recommendations} compensation={dashboard.compensation} onCreate={createForRole} onCreateJob={createForJob} generatingRoleId={generatingRoleId} />
                </div>
              </div>
              <div className="grid gap-4 lg:grid-cols-3 items-stretch">
                <div className="lg:col-span-2"><SkillGapPreview gap={dashboard.skill_gap_preview} /></div>
                <div className="lg:col-span-1"><ImmediateActions actions={snap?.immediate_actions} /></div>
              </div>
              <CareerPyramid resumeId={resumeId} />
              <div className="grid gap-4 lg:grid-cols-3 items-stretch">
                <div className={config.show_upgrade_ctas ? "lg:col-span-2" : "lg:col-span-3"}><ApplicationTracker /></div>
                {config.show_upgrade_ctas && <div className="lg:col-span-1"><UpgradeBridges compact /></div>}
              </div>
            </div>
          </div>
        )}

        {view === "workbench" && gen && (
          <TargetedResumeWorkbench
            key={gen.genId}
            mode={gen.mode}
            onRegenerate={gen.request ? () => generateResume({ ...gen.request, regenerate: true }, gen.key) : undefined}
            regenerating={!!generatingRoleId}
            membership={membership}
            onMembershipRefresh={refreshMembership}
            genId={gen.genId}
            roleTitle={gen.roleTitle}
            resume={gen.resume}
            immediateActions={snap?.immediate_actions}
            preferredTemplate={dashboard?.preferred_template}
            onBack={() => { setView(dashboard ? "dashboard" : "intake"); window.scrollTo(0, 0); }}
          />
        )}
      </main>
    </div>
  );
};

export default AppPage;
